import React, { useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { CheckCircle } from 'lucide-react';

const PreApplicationForm = () => {
  const [submitted, setSubmitted] = useState(false);
  const [applicationNumber, setApplicationNumber] = useState('');
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    positions_applying_for: [],
    first_name: '',
    last_name: '',
    telephone: '',
    email: '',
    address: '',
    apt: '',
    city_state_zip: '',
    eligibility_questions: {
      over_18: false,
      valid_license: false,
      reliable_transportation: false,
      health_condition: false,
      worked_with_tfs: false,
      worked_with_tfs_when: '',
      know_someone: false,
      know_someone_name: '',
      proper_documentation: false,
      currently_employed: false,
      current_employer: '',
      current_employer_phone: '',
      may_contact_employer: false,
      on_layoff: false,
      lift_35lbs: false,
      attend_orientation: false
    },
    availability_type: 'full_time',
    available_days: [],
    hours_not_available: '',
    referral_name_phone: '',
    skills_experience: {
      janitorial: {},
      landscaping: {},
      supervision: {},
      other: []
    },
    languages: [{ language: 'English', fluency: 'minimal', read: false, write: false }],
    employment_references: [
      { employer_name: '', supervisor_name: '', phone: '', position: '', dates_from: '', dates_to: '', reason_leaving: '', final_salary: '' },
      { employer_name: '', supervisor_name: '', phone: '', position: '', dates_from: '', dates_to: '', reason_leaving: '', final_salary: '' },
      { employer_name: '', supervisor_name: '', phone: '', position: '', dates_from: '', dates_to: '', reason_leaving: '', final_salary: '' }
    ],
    certifications: [],
    signature: '',
    date_signed: new Date().toISOString().split('T')[0]
  });

  const positionOptions = [
    { value: 'janitorial', label: 'Janitorial / Limpieza' },
    { value: 'landscaping', label: 'Landscaping / Jardinería' },
    { value: 'security', label: 'Security / Seguridad' },
    { value: 'sweeper', label: 'Sweeper / Barredora' },
    { value: 'maintenance', label: 'Maintenance / Mantenimiento' },
    { value: 'administration', label: 'Administration / Administración' }
  ];

  const handlePositionToggle = (position) => {
    const current = formData.positions_applying_for;
    if (current.includes(position)) {
      setFormData({
        ...formData,
        positions_applying_for: current.filter(p => p !== position)
      });
    } else {
      setFormData({
        ...formData,
        positions_applying_for: [...current, position]
      });
    }
  };

  const handleDayToggle = (day) => {
    const current = formData.available_days;
    if (current.includes(day)) {
      setFormData({
        ...formData,
        available_days: current.filter(d => d !== day)
      });
    } else {
      setFormData({
        ...formData,
        available_days: [...current, day]
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.positions_applying_for.length === 0) {
      toast.error('Please select at least one position');
      return;
    }

    if (!formData.signature) {
      toast.error('Signature is required');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/pre-applications`, formData);
      setApplicationNumber(response.data.application_number);
      setSubmitted(true);
      toast.success('Application submitted successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit application');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Application Submitted!</h2>
            <p className="text-gray-600 mb-4">
              Your pre-application has been submitted successfully.
            </p>
            <div className="bg-gray-50 p-4 rounded-lg mb-6">
              <p className="text-sm text-gray-600">Application Number:</p>
              <p className="text-xl font-bold text-gray-900">{applicationNumber}</p>
            </div>
            <p className="text-sm text-gray-600">
              We will review your application and contact you soon.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 py-8">
      <div className="max-w-4xl mx-auto">
        <Card className="mb-6">
          <CardHeader className="text-center bg-white border-b">
            <img 
              src="https://customer-assets.emergentagent.com/job_tfs-workspace/artifacts/xuj2h70u_TFSI%203-26%20PNG.png" 
              alt="TFS Logo" 
              className="h-16 mx-auto mb-4"
            />
            <CardTitle className="text-2xl">Pre-Employment Application</CardTitle>
            <p className="text-sm text-gray-600">Pre-Aplicación de Empleo</p>
          </CardHeader>
        </Card>

        <form onSubmit={handleSubmit}>
          {/* Position Selection */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Position Applying For / Posición</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {positionOptions.map((position) => (
                  <label key={position.value} className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.positions_applying_for.includes(position.value)}
                      onChange={() => handlePositionToggle(position.value)}
                      className="rounded"
                    />
                    <span className="text-sm">{position.label}</span>
                  </label>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* General Information */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>General Information / Información General</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Last Name / Apellido *</Label>
                  <Input
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label>First Name / Primer Nombre *</Label>
                  <Input
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label>Telephone / Teléfono *</Label>
                  <Input
                    value={formData.telephone}
                    onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label>Email / Correo Electrónico *</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                  />
                </div>
                <div className="md:col-span-2">
                  <Label>Address / Dirección *</Label>
                  <Input
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label>Apt / Apartamento</Label>
                  <Input
                    value={formData.apt}
                    onChange={(e) => setFormData({ ...formData, apt: e.target.value })}
                  />
                </div>
                <div>
                  <Label>City, State & ZIP</Label>
                  <Input
                    value={formData.city_state_zip}
                    onChange={(e) => setFormData({ ...formData, city_state_zip: e.target.value })}
                    required
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Eligibility Questions */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Eligibility Questions / Preguntas de Elegibilidad</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={formData.eligibility_questions.over_18}
                  onChange={(e) => setFormData({
                    ...formData,
                    eligibility_questions: { ...formData.eligibility_questions, over_18: e.target.checked }
                  })}
                  className="rounded"
                />
                <span className="text-sm">Over 18 years old? / ¿Mayor de 18 años?</span>
              </label>

              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={formData.eligibility_questions.valid_license}
                  onChange={(e) => setFormData({
                    ...formData,
                    eligibility_questions: { ...formData.eligibility_questions, valid_license: e.target.checked }
                  })}
                  className="rounded"
                />
                <span className="text-sm">Valid Driver's License? / ¿Licencia válida?</span>
              </label>

              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={formData.eligibility_questions.proper_documentation}
                  onChange={(e) => setFormData({
                    ...formData,
                    eligibility_questions: { ...formData.eligibility_questions, proper_documentation: e.target.checked }
                  })}
                  className="rounded"
                />
                <span className="text-sm">Proper documentation to work in U.S.? / ¿Documentos válidos?</span>
              </label>

              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={formData.eligibility_questions.lift_35lbs}
                  onChange={(e) => setFormData({
                    ...formData,
                    eligibility_questions: { ...formData.eligibility_questions, lift_35lbs: e.target.checked }
                  })}
                  className="rounded"
                />
                <span className="text-sm">Able to lift minimum of 35 lbs? / ¿Puede levantar 35 libras?</span>
              </label>
            </CardContent>
          </Card>

          {/* Availability */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Availability / Disponibilidad</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Type / Tipo</Label>
                <select
                  className="w-full border rounded-md px-3 py-2 mt-1"
                  value={formData.availability_type}
                  onChange={(e) => setFormData({ ...formData, availability_type: e.target.value })}
                >
                  <option value="part_time">Part Time / Medio Tiempo</option>
                  <option value="full_time">Full Time / Tiempo Completo</option>
                  <option value="any_time">Any Time / Cualquier horario</option>
                </select>
              </div>

              <div>
                <Label>Available Days / Días Disponibles</Label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
                  {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day) => (
                    <label key={day} className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.available_days.includes(day)}
                        onChange={() => handleDayToggle(day)}
                        className="rounded"
                      />
                      <span className="text-sm">{day}</span>
                    </label>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Acknowledgment & Signature */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Acknowledgment / Reconocimiento</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg text-sm space-y-4">
                <p>
                  <strong>English:</strong> I certify that all information provided in this Pre-Application is true, complete, and accurate to the best of my knowledge. I understand that providing false, misleading, or incomplete information—whether on this form or during any part of the hiring process—may disqualify me from consideration for employment.
                </p>
                <p>
                  I further understand that if any information provided is later found to be false, misleading, or incomplete, this may be considered grounds for immediate termination of employment, regardless of when the information is discovered.
                </p>
                <p>
                  By signing below, I acknowledge, understand, and agree to these terms, and I voluntarily consent to the use of this Pre-Application for employment consideration.
                </p>
                <hr className="my-4" />
                <p>
                  <strong>Español:</strong> Certifico que toda la información proporcionada en esta Pre-Aplicación es verdadera, completa y precisa según mi mejor conocimiento. Entiendo que proveer información falsa, engañosa o incompleta—ya sea en este formulario o en cualquier parte del proceso de contratación—puede descalificarme de ser considerado(a) para empleo.
                </p>
                <p>
                  Además, entiendo que si cualquier información proporcionada resulta ser falsa, engañosa o incompleta, esto podrá ser motivo de terminación inmediata de empleo, sin importar cuándo se descubra la información.
                </p>
                <p>
                  Al firmar abajo, reconozco, entiendo y acepto estos términos, y otorgo mi consentimiento voluntario para que esta Pre-Aplicación sea utilizada para consideración de empleo.
                </p>
              </div>

              <div>
                <Label>Applicant Signature / Firma del Aplicante *</Label>
                <Input
                  value={formData.signature}
                  onChange={(e) => setFormData({ ...formData, signature: e.target.value })}
                  placeholder="Type your full name as signature"
                  required
                />
              </div>

              <div>
                <Label>Today's Date / Fecha de Hoy</Label>
                <Input
                  type="date"
                  value={formData.date_signed}
                  onChange={(e) => setFormData({ ...formData, date_signed: e.target.value })}
                  required
                />
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-center mb-8">
            <Button type="submit" size="lg" disabled={loading} className="px-12">
              {loading ? 'Submitting...' : 'Submit Application / Enviar Aplicación'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PreApplicationForm;
